/* eslint-disable camelcase */
import React, { ReactElement, useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { InlineBellProps } from "./inlineBell.model";
import { DynamicForm } from "@companion-core/shared/app/Components/DynamicForm";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { useCreateRequest, useUpdateMember } from "@companion-core/shared/app/Queries";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { formatConciergeRequestPayload } from "@companion-core/shared/app/Utils/wizardHelper";
import { BellConfirmation } from "@companion-core/shared/app/Components/Bell/Confirmation";
import {
  getPreviousStep,
  hasFormPath,
  hasOnlyOneRadioShape,
} from "@companion-core/shared/app/Utils/dynamicForm";
import { CategoryHeader } from "@companion-core/shared/app/Components/Bell/CategoryHeader";
import { useWizardStore } from "@companion-core/shared/app/Store/wizardStore";
import { Button } from "@companion-core/shared/app/Components/Button";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";

import "@companion-core/web/src/Assets/Styles/scss/inlineBell.scss";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";

export const InlineBell = ({ bellFormPath, className, onClose }: InlineBellProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    vendorApiValues,
    clearBell,
    bellCategory,
    setPayload,
    payload: bellPayload,
  } = useBellStore();
  const {
    currentStep: currentFormStep,
    currentForm,
    formTree,
    setCurrentStep: setCurrentFormStep,
    setCurrentForm,
    resetWizard,
    setFields,
    setDefaultApiValues,
  } = useWizardStore((state) => ({
    formTree: state.formTree,
    currentForm: state.currentForm,
    currentStep: state.currentStep,
    resetWizard: state.resetWizard,
    setCurrentStep: state.setCurrentStep,
    setCurrentForm: state.setCurrentForm,
    setFields: state.setFields,
    setDefaultApiValues: state.setDefaultApiValues,
  }));

  const bellFormName = useMemo(() => {
    const splitPath = bellFormPath?.split("/");
    if (!splitPath) return "form";
    return splitPath[splitPath.length - 1].replace(".json", "");
  }, [bellFormPath]);
  const conciergeModule = getModule("concierge");
  const formPath = useMemo(
    () => conciergeModule?.modules?.inlineBell?.options?.registerFormPath,
    [conciergeModule]
  );
  const registerEnabled = useMemo(
    () => conciergeModule?.modules?.inlineBell?.options?.withRegister,
    [conciergeModule]
  );
  const registerJSONForm = useMemo(() => loadFormFromPath(formPath), [formPath]);
  const conciergeJSONForm = useMemo(() => loadFormFromPath(bellFormPath), [bellFormPath]);
  const queryClient = useQueryClient();
  const member = queryClient.getQueryData<Record<string, unknown>>(["member"]);
  const { mutateAsync: updateMember } = useUpdateMember();
  const [currentStep, setCurrentStep] = useState("");
  const { mutateAsync: sendBellRequest } = useCreateRequest();
  const canGoBack = useMemo(
    () =>
      currentStep === "concierge" &&
      currentForm &&
      currentFormStep?.order &&
      getPreviousStep(currentForm, currentFormStep.order, formTree).step,
    [currentStep, currentForm, currentFormStep, formTree]
  );

  useEffect(() => {
    resetWizard();
  }, []);

  useEffect(() => {
    if (!member?.lastname && registerEnabled) {
      setCurrentStep("register");
    } else {
      setCurrentStep("concierge");
    }
  }, [member]);

  const onSubmitRegister = async (payload: Record<string, unknown>) => {
    payload = {
      ...payload,
      addresses: [
        {
          address_complement: null,
          city: null,
          country_code: payload.country,
          state: null,
          street: null,
          type: "personal",
          zip_code: null,
        },
      ],
    };
    delete payload.country;
    try {
      await updateMember({ id: "me", payload });
      setCurrentStep("concierge");
    } catch {
      setCurrentStep("error-register");
    }
  };

  const onSubmitConcierge = async (payload: Record<string, unknown>, refetch = false) => {
    if (!refetch) {
      setPayload(payload);
    }
    const category: string = payload?.family
      ? (payload.family as string)
      : vendorApiValues?.family
        ? (vendorApiValues?.family as string)
        : (bellCategory?.category ?? "");
    const formattedPayload: Record<string, unknown> = formatConciergeRequestPayload(
      {
        ...vendorApiValues,
        ...payload,
      },
      category
    );
    try {
      await sendBellRequest({ payload: formattedPayload });
      setCurrentStep("validation");
    } catch {
      setCurrentStep("error-concierge");
    }
  };

  const goBack = () => {
    if (currentForm && currentFormStep?.order) {
      const { step, jsonForm } = getPreviousStep(currentForm, currentFormStep.order, formTree);
      const hasFormPathInField = step?.fields.some((field) => hasFormPath(field));
      setCurrentForm(jsonForm);
      if (step && hasOnlyOneRadioShape(step)) step.fields[0].value = "";
      if (hasFormPathInField) {
        setFields([]);
        setDefaultApiValues({ ...jsonForm.defaultApiValues });
        setCurrentFormStep(step);
      } else if (step) {
        setCurrentFormStep(step);
      }
    }
  };

  const closeInlineBell = () => {
    clearBell();
    resetWizard();
    onClose?.();
  };

  const inlineBellContentMap: Record<string, ReactElement> = {
    "register": (
      <div className="inline-bell__register">
        <DynamicForm
          initialFormJSON={registerJSONForm}
          name="inline-register"
          onSubmitForm={onSubmitRegister}
        />
      </div>
    ),
    "concierge": (
      <div className="inline-bell__bell">
        <CategoryHeader category={bellCategory} />
        <div className="inline-bell__bell-form">
          <DynamicForm
            initialFormJSON={conciergeJSONForm}
            name={bellFormName}
            onSubmitForm={onSubmitConcierge}
            submitButtonAccessibilityLabel={t("request.concierge.form.submit.accessibilityLabel")}
          />
        </div>
      </div>
    ),
    "validation": (
      <BellConfirmation requestStatus="success" onClick={() => navigate("/my-requests")} />
    ),
    "error-register": (
      <>
        <div data-testid="inline-bell__register-error" className="bell__confirmation">
          <div className="bell__confirmation-container">
            <div className="bell__confirmation-icon">
              <FontIcon name="JP-Circle-close" size={46} />
            </div>
            <div className="bell__confirmation-title title-2">
              {t("register.error.unknown.title")}
            </div>
            <div className="bell__confirmation-subtitle body-text-6">
              <div
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(t("register.error.unknown.text")) }}
              />
            </div>
          </div>
          <div className="bell__confirmation-button">
            <Button
              testID="inline-bell__register-error-button"
              type="primary"
              size="full"
              label={t("register.error.unknown.button")}
              onClick={() => setCurrentStep("register")}
            />
          </div>
        </div>
      </>
    ),
    "error-concierge": (
      <BellConfirmation
        requestStatus="error"
        onClick={() => onSubmitConcierge(bellPayload, true)}
      />
    ),
  };

  return (
    <div className={`inline-bell${className ? " " + className : ""}`}>
      <div className="inline-bell__header">
        {canGoBack && (
          <div
            onClick={goBack}
            role="button"
            className="inline-bell__header-button"
            data-testid="inline-bell__header-back-button">
            <FontIcon name="JP-Arrow-left-bold" size={24} />
          </div>
        )}
        <div className="body-text-5 inline-bell__header-placeholder" />
        <div
          onClick={() => closeInlineBell()}
          role="button"
          className="inline-bell__header-button"
          data-testid="inline-bell__header-close-button">
          <FontIcon name="JP-Close-bold" size={24} />
        </div>
      </div>
      <div className="inline-bell__content">{inlineBellContentMap[currentStep]}</div>
    </div>
  );
};
